import { Star } from 'lucide-react';
import React from 'react';
import ReviewBadge from '../../lib/ReviewBadge/ReviewBadge';
import { Avatar } from '../Avatar/Avatar';
import { Flex } from '../Flex/Flex';
import { Img } from '../Img/Img';
import { ShowIf } from '../ShowIf/ShowIf';
import { StarRating } from '../StarRating/StarRating';

interface IReviewProps {
  name: string;
  date: string;
  rating?: number;
  comments?: string;
  media?: string[];
  mediaItemWidthClassName?: string;
  reply?: IReviewProps;
  className?: string;
  onMediaClick?: (e?: any) => void;
  /** Profile page layout: tighter header, black stars, Geist typography. */
  layout?: 'default' | 'profile';
}

export const Review: React.FC<React.PropsWithChildren<IReviewProps>> = ({
  name,
  date,
  rating,
  comments,
  media = [],
  reply,
  mediaItemWidthClassName = 'w-5/12 lg:w-2/12',
  onMediaClick,
  className = '',
  layout = 'default',
}) => {
  const profile = layout === 'profile';
  const avatarPhoto = (media.length && media[0]) || undefined;
  const galleryMedia = profile && media.length > 1 ? media.slice(1) : media;

  return (
    <div className={`w-full ${className}`}>
      <AvatarSection
        name={name.split(' ')[0]}
        date={date}
        photo={avatarPhoto}
        score={rating}
        layout={layout}
      />
      <Comment
        className={`flex-grow ${profile ? 'my-2' : 'my-5'}`}
        comment={comments}
        layout={layout}
      />
      {/* <div className="flex-grow my-3">{comments}</div> */}
      {!!reply && (
        <div className="pl-6 mt-2 border-l-2">
          {/* <Flex>
            <Avatar className="text-base" size="8" name={reply.name} />
            <div className="px-4 ">
              <p className="mb-1">{reply.name.split(' ')[0]}</p>
              <p className="text-gray-500">{reply.date}</p>
            </div>
          </Flex> */}
          <AvatarSection
            name={reply.name.split(' ')[0]}
            date={reply.date}
            layout={layout}
          />
          <Comment
            className="flex-grow pl-4 mt-1"
            comment={reply.comments}
            layout={layout}
          />
        </div>
      )}

      {!!galleryMedia.length && (
        <div
          className={`flex w-full overflow-x-auto flex-nowrap ${
            profile ? 'mt-4 gap-3 space-x-0' : 'mt-5 space-x-5'
          }`}
        >
          {galleryMedia.map((m, idx) => (
            <div
              key={m + idx}
              className={`flex-none ${mediaItemWidthClassName}`}
            >
              <Img
                src={m}
                size="square"
                roundedCorners={profile ? 'lg' : 'xl'}
                shadow
                onClick={onMediaClick}
              />
            </div>
          ))}
        </div>
      )}

      {/* <ShowIf condition={media?.length}>
        <div className="w-full mt-auto overflow-hidden">
          <Flex className="w-full overflow-x-auto" wrap={false} y="start">
            <Flex className="w-full px-1" wrap={false}>
              {media.map((m, idx) => (
                <div
                  key={m + idx}
                  className={`flex-none mr-4 ${mediaItemWidthClassName}`}
                >
                  <Img
                    src={m}
                    size="square"
                    roundedCorners
                    shadow
                    onClick={onMediaClick}
                  />
                </div>
              ))}
              <div className="invisible">|</div>
            </Flex>
          </Flex>
        </div>
      </ShowIf> */}
    </div>
  );
};

function AvatarSection({
  name,
  photo = '',
  date,
  score = 0,
  layout = 'default',
}: {
  name: string;
  photo?: string;
  date: string;
  score?: number;
  layout?: 'default' | 'profile';
}) {
  const profile = layout === 'profile';
  const filled = Math.min(5, Math.max(0, Math.round(score || 0)));

  return (
    <Flex className={profile ? 'items-center' : undefined}>
      <Avatar
        img={photo}
        className={`flex-none capitalize ${profile ? 'text-base' : 'text-xl'}`}
        size={profile ? '8' : '14'}
        name={name}
      />
      <div className={`min-w-0 flex-1 ${profile ? 'pl-3 pr-2' : 'px-4'}`}>
        <p
          className={`capitalize text-black ${
            profile
              ? 'mb-0.5 font-geist text-[13px] font-medium leading-snug'
              : 'mb-1 text-lg leading-[1.5em]'
          }`}
        >
          {name}
        </p>
        <p
          className={
            profile
              ? 'font-geist text-[11px] font-normal leading-snug text-[#8E8E93]'
              : 'text-sm text-black'
          }
        >
          {date}
        </p>
      </div>
      {!!score &&
        (profile ? (
          <div className="ml-auto flex shrink-0 gap-0.5" aria-hidden>
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`h-[10px] w-[10px] ${
                  i <= filled
                    ? 'fill-black text-black'
                    : 'fill-gray-200 text-gray-200'
                }`}
                strokeWidth={0}
              />
            ))}
          </div>
        ) : (
          <div className="ml-auto font-medium">
            <ReviewBadge className="text-gray-primary" score={score} />
          </div>
        ))}
    </Flex>
  );
}

function Comment({
  comment,
  className,
  layout = 'default',
}: {
  comment?: string;
  className?: string;
  layout?: 'default' | 'profile';
}) {
  const profile = layout === 'profile';
  return (
    <div
      className={
        className +
        (profile
          ? ' font-geist text-[13px] font-normal leading-[1.7em] text-black'
          : ' text-sm text-gray-5E lg:text-lg leading-[1.8em]')
      }
    >
      {comment}
    </div>
  );
}
